import httpClient from './http/httpClient';

export interface ContextHunterResponse<_T = unknown> {
  success: boolean;
  error?: string;
  [key: string]: unknown;
}

export const scanStorage = async (params: {
  userId: string;
  clientId: string;
  path?: string;
  folderPaths?: string[];
  siteId?: string;
  driveId?: string;
  mode?: string;
}) => {
  const response = await httpClient.post<ContextHunterResponse>('/context-hunter-service', {
    action: 'scan',
    mode: params.mode || 'context_hunter',
    ...params,
  });
  return response;
};

export const indexStorage = async (params: {
  userId: string;
  clientId: string;
  folderPaths: string[];
  siteId?: string;
  driveId?: string;
  mode?: string;
  collectionType?: string;
}) => {
  const response = await httpClient.post<ContextHunterResponse>('/context-hunter-service', {
    action: 'index',
    mode: params.mode || 'context_hunter',
    ...params,
  });
  return response;
};

const debouncedSyncTimers = new Map<string, ReturnType<typeof setTimeout>>();
const debouncedSyncPaths = new Map<string, Set<string>>();

/**
 * Coalesces rapid-fire upload/move/folder changes for the same (clientId, collectionType)
 * into a single background indexStorage request after `delayMs` of quiet time.
 */
export const scheduleDebouncedIndexStorage = (
  params: Parameters<typeof indexStorage>[0],
  delayMs = 45000
) => {
  const key = `${params.clientId}:${params.collectionType || params.mode || 'context_hunter'}`;
  const existingTimer = debouncedSyncTimers.get(key);
  if (existingTimer) {
    clearTimeout(existingTimer);
  }

  const pathSet = debouncedSyncPaths.get(key) || new Set<string>();
  for (const p of params.folderPaths) {
    pathSet.add(p);
  }
  debouncedSyncPaths.set(key, pathSet);

  const timer = setTimeout(() => {
    const mergedPaths = Array.from(debouncedSyncPaths.get(key) || params.folderPaths);
    debouncedSyncTimers.delete(key);
    debouncedSyncPaths.delete(key);
    void indexStorage({
      ...params,
      folderPaths: mergedPaths,
    }).catch(() => {
      // Background sync errors are logged server-side
    });
  }, delayMs);

  debouncedSyncTimers.set(key, timer);
};

export const resetStorageIndex = async (params: {
  userId: string;
  clientId: string;
  folderPaths: string[];
  siteId?: string;
  driveId?: string;
  mode?: string;
}) => {
  const response = await httpClient.post<ContextHunterResponse>('/context-hunter-service', {
    action: 'reset',
    mode: params.mode || 'context_hunter',
    ...params,
  });
  return response;
};

export const resolveExternalSite = async (params: {
  userId: string;
  clientId: string;
  siteUrl: string;
  mode?: string;
}) => {
  const response = await httpClient.post<ContextHunterResponse>('/context-hunter-service', {
    action: 'resolve_external_site',
    mode: params.mode || 'context_hunter',
    ...params,
  });
  return response;
};

export const listFolders = async (params: {
  userId: string;
  clientId: string;
  path: string;
  siteId?: string;
  driveId?: string;
  mode?: string;
}) => {
  const response = await httpClient.post<ContextHunterResponse>('/context-hunter-service', {
    action: 'list_folders',
    mode: params.mode || 'context_hunter',
    ...params,
  });
  return response;
};

export const huntContext = async (params: {
  userId: string;
  clientId: string;
  tenderId: string;
  folderPaths?: string[];
  siteId?: string;
  driveId?: string;
  maxResults?: number;
  minScore?: number;
  mode?: string;
}) => {
  const response = await httpClient.post<ContextHunterResponse>('/context-hunter-service', {
    action: 'hunt',
    mode: params.mode || 'context_hunter',
    ...params,
  });
  return response;
};

export const searchContextFiles = async (params: {
  userId: string;
  clientId: string;
  tenderId: string;
  query: string;
  folderPaths?: string[];
  siteId?: string;
  driveId?: string;
  maxResults?: number;
  mode?: string;
}) => {
  const response = await httpClient.post<ContextHunterResponse>('/context-hunter-service', {
    action: 'search_files',
    mode: params.mode || 'context_hunter',
    ...params,
  });
  return response;
};

export const moveKnowledgeDocument = async (params: {
  docId: string;
  sourceCollectionType: string;
  targetCollectionType: string;
  userId: string;
  clientId: string;
}) => {
  const response = await httpClient.post<{
    success: boolean;
    message?: string;
    storagePath?: string;
    docId?: string;
  }>('/moveKnowledgeDocument', params);
  return response;
};
